/**
 * AppointmentPage.jsx — Al Barede Law Firm
 * ─────────────────────────────────────────────────────────────────────────────
 * 3-step appointment booking wizard.
 *   Step 1 → Personal Information + Consultation Type
 *   Step 2 → Select Date → fetch office slots → select time
 *   Step 3 → Confirmation screen
 *
 * Usage:  <AppointmentPage apiBase="http://localhost/server/testServer" />
 *
 * FIXES vs previous version:
 *   [FIX-1] useGlobalStyles() was injecting an empty <style> tag — hook removed
 *           entirely since CSS is loaded via  import './AppointmentPage.css'
 *   [FIX-2] Calendar divider used hardcoded  borderRight: '1px solid #000'
 *           — now uses the CSS class  .ab-cal-side  (defined in the CSS file)
 *   [FIX-3] Inline styles using 'var(--gold)' / 'var(--text-muted)' now work
 *           because those variables are defined in AppointmentPage.css :root
 *   [FIX-4] Endpoint changed from practice_areas.php → consultation_types.php
 *           to match the actual DB table and the new backend file
 *   [FIX-5] Footer added (was missing in the React file)
 */

import './AppointmentPage.css';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';


// ─── API Base URL — update to match your server path ─────────────────────────
// const DEFAULT_API = 'http://localhost/server/testServer';
const DEFAULT_API = 'https://www.albarede.com/server';

// ─── Fallback types shown while the fetch is in flight ───────────────────────
const FALLBACK_TYPES = [
  { id: 1,  name: 'Corporate Law' },
  { id: 2,  name: 'Real Estate Law' },
  { id: 3,  name: 'Criminal Defense' },
  { id: 4,  name: 'Family Law' },
  { id: 5,  name: 'Intellectual Property' },
  { id: 6,  name: 'Tax Law' },
  { id: 7,  name: 'Immigration Law' },
  { id: 8,  name: 'Employment Law' },
  { id: 9,  name: 'Civil Litigation' },
  { id: 10, name: 'General Consultation' },
];

// ─── Utilities ────────────────────────────────────────────────────────────────
const pad       = (n)  => String(n).padStart(2, '0');
const toISODate = (d)  => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const fmtDisplay = (dateStr) =>
  new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

const fmtTime = (t) => {
  const [hh, mm] = t.split(':');
  const h = parseInt(hh, 10);
  return `${h > 12 ? h - 12 : h || 12}:${mm} ${h >= 12 ? 'PM' : 'AM'}`;
};

// ═════════════════════════════════════════════════════════════════════════════
// SVG ICONS
// ═════════════════════════════════════════════════════════════════════════════
const ChevL = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);
const ChevR = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const CheckIco = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const ScaleIco = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="3" x2="12" y2="21"/>
    <path d="M5 12l7-7 7 7"/>
    <path d="M2 17h5m10 0h5"/>
  </svg>
);

// ═════════════════════════════════════════════════════════════════════════════
// SHARED ATOMS
// ═════════════════════════════════════════════════════════════════════════════
function SectionTitle({ title, sub }) {
  return (
    <div className="ab-title">
      <h2>{title}</h2>
      {sub && <p>{sub}</p>}
      <div className="ab-rule" />
    </div>
  );
}

function Spinner({ label }) {
  return (
    <div className="ab-loading">
      <div className="ab-spinner" />
      {label && <span>{label}</span>}
    </div>
  );
}

function ErrorMsg({ msg }) {
  return <div className="ab-error">{msg}</div>;
}

// ═════════════════════════════════════════════════════════════════════════════
// STEPPER
// ═════════════════════════════════════════════════════════════════════════════
const STEPS = ['Your Details', 'Date & Time', 'Confirmed'];

function Stepper({ current }) {
  return (
    <div className="ab-stepper" role="list">
      {STEPS.map((lbl, i) => {
        const idx    = i + 1;
        const done   = idx < current;
        const active = idx === current;
        return (
          <div key={i}
            className={`ab-step${active ? ' active' : ''}${done ? ' done' : ''}`}
            role="listitem"
            aria-current={active ? 'step' : undefined}
          >
            <div className="ab-step-circle">
              {done ? <CheckIco /> : idx}
            </div>
            <span className="ab-step-lbl">{lbl}</span>
          </div>
        );
      })}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// STEP 1 — Personal Information
// ═════════════════════════════════════════════════════════════════════════════
const EMPTY_FORM = {
  first: '', last: '', email: '', phone: '',
  consultation_type_id: '', notes: '',
};

function StepInfo({ apiBase, form, setForm, onNext }) {
  const [types, setTypes] = useState(FALLBACK_TYPES);
  const [error, setError] = useState('');

  // FIX-4: fetch consultation_types.php, not practice_areas.php
  useEffect(() => {
    fetch(`${apiBase}/consultation_types.php`)
      .then(r => r.json())
      .then(d => { if (d.success && d.data.length) setTypes(d.data); })
      .catch(() => {}); // fallback stays on network error
  }, [apiBase]);

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleNext = () => {
    if (!form.first.trim()) return setError('First name is required.');
    if (!form.last.trim())  return setError('Last name is required.');
    if (!form.email.trim()) return setError('Email address is required.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return setError('Please enter a valid email address.');
    setError('');
    onNext();
  };
  const { t, i18n } = useTranslation();
  return (
    <>
      <SectionTitle
        title={t('Personal_Information')}
        sub={t('Poersnal_disc')}
      />
      <div className="ab-card">
        <div className="ab-form-body">
          {error && <ErrorMsg msg={error} />}
          <div className="ab-grid">

            <div className="ab-field">
              <label className="ab-label">{t('appointment_firstName')}<span className="req">*</span></label>
              <input className="ab-input" value={form.first} onChange={set('first')}
                placeholder={t('formName')} autoComplete="given-name" />
            </div>

            <div className="ab-field">
              <label className="ab-label">{t('appointment_lastName')}<span className="req">*</span></label>
              <input className="ab-input" value={form.last} onChange={set('last')}
                placeholder={t('formLastName')} autoComplete="family-name" />
            </div>

            <div className="ab-field">
              <label className="ab-label">{t('Email_Address')} <span className="req">*</span></label>
              <input className="ab-input" type="email" value={form.email} onChange={set('email')}
                placeholder="you@example.com" autoComplete="email" />
            </div>

            <div className="ab-field">
              <label className="ab-label">{t('Phone_Number')}</label>
              <input className="ab-input" type="tel" value={form.phone} onChange={set('phone')}
                placeholder="+900 000 0000" autoComplete="tel" />
            </div>

            <div className="ab-field full">
              <label className="ab-label">{t('Consultation_Type')}</label>
              <select className="ab-select" value={form.consultation_type_id}
                onChange={set('consultation_type_id')}>
                <option value="">Select area of law…</option>
                {types.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            <div className="ab-field full">
              <label className="ab-label">
                {t('Notes')}&nbsp;
                <span style={{ color: 'var(--text-muted)', fontStyle: 'italic',
                               textTransform: 'none', letterSpacing: 0 }}>
                  (optional)
                </span>
              </label>
              <textarea className="ab-textarea" value={form.notes} onChange={set('notes')}
                placeholder={t('Notes_placeHolder')}
                rows={4} />
            </div>

          </div>
        </div>
      </div>

      <div className="ab-nav">
        <span style={{ fontSize: '.68rem', color: 'var(--text-muted)' }}>
          {t('information_save')}
        </span>
        <button className="ab-btn ab-btn-primary ab-nav-r" onClick={handleNext}>
          {t('Select_Date')} &amp; {t('Time')} <ChevR />
        </button>
      </div>
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// STEP 2 — Date & Time
// ═════════════════════════════════════════════════════════════════════════════
function StepDateTime({
  apiBase, selectedDate, setDate,
  selectedTime, setTime,
  onBack, onSubmit, submitting, submitError,
}) {
  const [viewDate,     setViewDate]     = useState(new Date());
  const [slots,        setSlots]        = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsError,   setSlotsError]   = useState('');
  const prevDateRef = useRef('');

  // Fetch slots whenever selectedDate changes
  useEffect(() => {
    if (!selectedDate || selectedDate === prevDateRef.current) return;
    prevDateRef.current = selectedDate;
    setSlots(null);
    setSlotsError('');
    setTime('');
    setLoadingSlots(true);

    fetch(`${apiBase}/get_slots.php?date=${selectedDate}`)
      .then(r => r.json())
      .then(d => {
        if (d.success) setSlots(d.data);
        else setSlotsError(d.error || 'Unable to load time slots.');
      })
      .catch(() => setSlotsError('Network error loading slots. Please try again.'))
      .finally(() => setLoadingSlots(false));
  }, [selectedDate, apiBase, setTime]);

  // Calendar computation
  const year       = viewDate.getFullYear();
  const month      = viewDate.getMonth();
  const today      = new Date(); today.setHours(0, 0, 0, 0);
  const todayStr   = toISODate(today);
  const firstDow   = new Date(year, month, 1).getDay();
  const daysInMo   = new Date(year, month + 1, 0).getDate();
  const prevDaysIn = new Date(year, month, 0).getDate();
  const monthLabel = viewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const cells = (() => {
    const c = [];
    // Trailing days from previous month
    for (let i = firstDow - 1; i >= 0; i--)
      c.push({ n: prevDaysIn - i, cur: false, dateStr: null });
    // Current month
    for (let d = 1; d <= daysInMo; d++) {
      const dt  = new Date(year, month, d);
      const str = toISODate(dt);
      c.push({ n: d, cur: true, past: dt < today, dateStr: str });
    }
    // Leading days of next month to fill 6×7 grid
    for (let d = 1; c.length < 42; d++)
      c.push({ n: d, cur: false, dateStr: null });
    return c;
  })();

  const handleDayClick = (cell) => {
    if (!cell.cur || cell.past) return;
    setDate(cell.dateStr);
  };

  const canSubmit = selectedDate && selectedTime && !submitting;
  const { t, i18n } = useTranslation();
  return (
    <>
      <SectionTitle
        title={t('Select_Date_Time')}
        sub={t('Choose_prefernd')}
      />

      {submitError && <ErrorMsg msg={submitError} />}

      <div className="ab-card">
        {/* FIX-2: grid wrapper; cal side uses .ab-cal-side class for the border */}
        <div className="ab-cal-wrap">

          {/* ── Calendar ── */}
          <div className="ab-cal-side">
            <div className="ab-cal-hdr">
              <button className="ab-nav-btn"
                onClick={() => setViewDate(new Date(year, month - 1, 1))}
                aria-label="Previous month">
                <ChevL />
              </button>
              <span className="ab-cal-month">{monthLabel}</span>
              <button className="ab-nav-btn"
                onClick={() => setViewDate(new Date(year, month + 1, 1))}
                aria-label="Next month">
                <ChevR />
              </button>
            </div>

            <div className="ab-cal-grid">
              <div className="ab-dow">
                {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d =>
                  <span key={d}>{d}</span>
                )}
              </div>
              <div className="ab-days">
                {cells.map((cell, i) => {
                  let cls = 'ab-day';
                  if (!cell.cur)                       cls += ' other';
                  else if (cell.past)                   cls += ' past';
                  else                                  cls += ' avail';
                  if (cell.dateStr === selectedDate)    cls += ' selected';
                  if (cell.dateStr === todayStr)        cls += ' today';
                  return (
                    <div key={i} className={cls}
                      onClick={() => handleDayClick(cell)}
                      role={cell.cur && !cell.past ? 'button' : undefined}
                      tabIndex={cell.cur && !cell.past ? 0 : undefined}
                      onKeyDown={e => e.key === 'Enter' && handleDayClick(cell)}
                    >
                      {cell.n}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Time Slots ── */}
          <div className="ab-slots-panel">
            <div className="ab-slots-hdr">
              <h3>
                {selectedDate
                  ? new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US',
                      { weekday: 'long', month: 'long', day: 'numeric' })
                  : 'Available Times'}
              </h3>
            </div>

            <div className="ab-slots-body">
              {!selectedDate ? (
                <p className="ab-slots-prompt">
                  {t('ab-slots-prompt')}
                </p>
              ) : loadingSlots ? (
                <Spinner label="Loading available times…" />
              ) : slotsError ? (
                <ErrorMsg msg={slotsError} />
              ) : slots && (slots.morning.length + slots.evening.length) === 0 ? (
                <p className="ab-slots-empty">
                  {t('ab-slots-empty')}
                </p>
              ) : slots ? (
                <>
                  {slots.morning.length > 0 && (
                    <div className="ab-slots-section">
                      <label>Morning</label>
                      <div className="ab-slots-grid">
                        {slots.morning.map(t => (
                          <button key={t}
                            className={`ab-slot-btn${selectedTime === t ? ' sel' : ''}`}
                            onClick={() => setTime(t)}>
                            {fmtTime(t)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {slots.evening.length > 0 && (
                    <div className="ab-slots-section">
                      <label>Evening</label>
                      <div className="ab-slots-grid">
                        {slots.evening.map(t => (
                          <button key={t}
                            className={`ab-slot-btn${selectedTime === t ? ' sel' : ''}`}
                            onClick={() => setTime(t)}>
                            {fmtTime(t)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </div>

        </div>
      </div>

      <div className="ab-nav" style={{ marginTop: 24 }}>
        <button className="ab-btn ab-btn-ghost" onClick={onBack}>
          <ChevL /> Back
        </button>
        <button className="ab-btn ab-btn-primary ab-nav-r"
          disabled={!canSubmit} onClick={onSubmit}>
          {submitting ? 'Booking…' : 'Confirm Appointment →'}
        </button>
      </div>
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// STEP 3 — Confirmed
// ═════════════════════════════════════════════════════════════════════════════
function StepConfirmed({ booking, form, types, onReset }) {
  const typeName =
    types.find(t => String(t.id) === String(form.consultation_type_id))?.name
    || 'General Consultation';
  const { t, i18n } = useTranslation();

  return (
    <div className="ab-card">
      <div className="ab-confirm">
        <div className="ab-confirm-icon">✓</div>
        <h2>{t('Appointment_Confirmed')}</h2>
        {/* FIX-3: var(--gold) works because CSS vars are in AppointmentPage.css */}
        <p className="sub">
          {t('sub')}{' '}
          <strong style={{ color: 'var(--gold)' }}>{form.email}</strong>.<br />
          {t('Please_verify')}
        </p>

        <div className="ab-detail-box">
          <div className="ab-detail-row">
            <span className="lbl">{t('Client')}</span>
            <span className="val">{form.first} {form.last}</span>
          </div>
          <div className="ab-detail-row">
            <span className="lbl">{t('Date')}</span>
            <span className="val">{fmtDisplay(booking.appointment_date)}</span>
          </div>
          <div className="ab-detail-row">
            <span className="lbl">{t('Time')}</span>
            <span className="val">{fmtTime(booking.appointment_time)}</span>
          </div>
          <div className="ab-detail-row">
            <span className="lbl">{t('Consultation')}</span>
            <span className="val">{typeName}</span>
          </div>
          <div className="ab-detail-row">
            <span className="lbl">{t('Location')}</span>
            <span className="val">Al Barede Law Firm Main Office</span>
          </div>
          <div className="ab-detail-row">
            <span className="lbl">Status</span>
            <span className="val" style={{ color: 'var(--gold)' }}>Pending Confirmation</span>
          </div>
        </div>

        <div className="ab-token-box">
          <div className="tlbl">Confirmation Reference</div>
          <div className="tval">{booking.confirmation_token}</div>
        </div>

        <button className="ab-btn ab-btn-ghost" style={{ width: '100%' }} onClick={onReset}>
          {t('Book_Another')}
        </button>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ROOT COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
export default function AppointmentPage({ apiBase = DEFAULT_API }) {
  // FIX-1: useGlobalStyles() removed — CSS is loaded via the import at the top

  const [step,        setStep]        = useState(1);
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [selectedDate, setDate]       = useState('');
  const [selectedTime, setTime]       = useState('');
  const [booking,     setBooking]     = useState(null);
  const [submitting,  setSubmitting]  = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [types,       setTypes]       = useState(FALLBACK_TYPES);

  // Fetch types for confirmation screen label
  useEffect(() => {
    fetch(`${apiBase}/consultation_types.php`)
      .then(r => r.json())
      .then(d => { if (d.success && d.data.length) setTypes(d.data); })
      .catch(() => {});
  }, [apiBase]);

  const reset = useCallback(() => {
    setStep(1); setForm(EMPTY_FORM);
    setDate(''); setTime(''); setBooking(null);
    setSubmitting(false); setSubmitError('');
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!selectedDate || !selectedTime) return;
    setSubmitting(true);
    setSubmitError('');

    try {
      const res = await fetch(`${apiBase}/book_appointment.php`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_first_name:    form.first.trim(),
          client_last_name:     form.last.trim(),
          client_email:         form.email.trim(),
          client_phone:         form.phone.trim()  || undefined,
          consultation_type_id: form.consultation_type_id
                                  ? Number(form.consultation_type_id)
                                  : undefined,
          notes:                form.notes.trim()  || undefined,
          appointment_date:     selectedDate,
          appointment_time:     selectedTime,
        }),
      });

      const data = await res.json();
      if (data.success) {
        setBooking(data.data);
        setStep(3);
      } else {
        setSubmitError(data.error || 'Booking failed. Please try again.');
      }
    } catch {
      setSubmitError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }, [apiBase, form, selectedDate, selectedTime]);

  return (
    <div className="ab-wrap">

      {/* ── Header ── */}
      <header className="ab-header">
        <Stepper current={step} />
      </header>

      {/* ── Main ── */}
      <main className="ab-main">

        {step === 1 && (
          <StepInfo
            apiBase={apiBase}
            form={form}
            setForm={setForm}
            onNext={() => setStep(2)}
          />
        )}

        {step === 2 && (
          <StepDateTime
            apiBase={apiBase}
            selectedDate={selectedDate}
            setDate={setDate}
            selectedTime={selectedTime}
            setTime={setTime}
            onBack={() => setStep(1)}
            onSubmit={handleSubmit}
            submitting={submitting}
            submitError={submitError}
          />
        )}

        {step === 3 && (
          <>
            <SectionTitle title="You Are Confirmed" sub="" />
            <StepConfirmed
              booking={booking}
              form={form}
              types={types}
              onReset={reset}
            />
          </>
        )}

      </main>

      {/* FIX-5: Footer restored */}
      <footer className="ab-footer">
        All consultations are protected by attorney-client privilege and strict professional confidentiality.
        &nbsp;·&nbsp; Al Barede Law Firm &nbsp;·&nbsp; info@albarede.com
      </footer>

    </div>
  );
}